import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './About.css';

const About: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStartListing = () => {
    if (user) {
      navigate('/my-items');
    } else {
      navigate('/login');
    }
  };

  const handleBrowseItems = () => {
    navigate('/');
  };

  return (
    <div className="about-page">
      <div className="about-header">
        <h1>About Share My Shed</h1>
        <p>Unlocking the value in your unused items while building stronger communities</p>
      </div>

      <div className="about-content">
        {/* Mission Section */}
        <section className="about-section">
          <h2>Our Mission</h2>
          
          <div className="mission-content">
            <div className="mission-text">
              <p className="mission-statement">
                <strong>Share My Shed</strong> exists to help people unlock the earning potential of their expensive, 
                infrequently used items while providing affordable access to tools and equipment for those who need them.
              </p>
              <p>
                We believe that the average household has hundreds or even thousands of pounds worth of items 
                sitting idle in sheds, garages, and storage spaces. From power tools and garden equipment to 
                specialized gear and electronics, these items represent untapped income potential.
              </p>
              <p>
                Our platform transforms your unused assets into a steady income stream while helping neighbors 
                access the tools they need without the burden of ownership.
              </p>
            </div>
            
            <div className="mission-stats">
              <div className="stat-card">
                <div className="stat-number">£2,500</div>
                <div className="stat-label">Average value of unused items per household</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">85%</div>
                <div className="stat-label">Of tools are used less than 13 minutes per year</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">£500+</div>
                <div className="stat-label">Monthly earnings potential for active lenders</div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="about-section">
          <h2>How We're Changing Everything</h2>
          
          <div className="process-cards">
            <div className="process-card">
              <div className="process-icon">🏠</div>
              <h3>Monetize Your Assets</h3>
              <p>
                Turn your shed, garage, or storage space into a revenue-generating asset. 
                List items you own but rarely use and start earning money from day one.
              </p>
              <ul>
                <li>Power tools and garden equipment</li>
                <li>Sports and outdoor gear</li>
                <li>Electronics and appliances</li>
                <li>Specialized equipment and machinery</li>
              </ul>
            </div>

            <div className="process-card">
              <div className="process-icon">🤝</div>
              <h3>Build Community</h3>
              <p>
                Connect with neighbors and build trust within your local community. 
                Share resources, reduce waste, and create meaningful relationships.
              </p>
              <ul>
                <li>Meet local neighbors and makers</li>
                <li>Share knowledge and expertise</li>
                <li>Reduce environmental impact</li>
                <li>Strengthen community bonds</li>
              </ul>
            </div>

            <div className="process-card">
              <div className="process-icon">💰</div>
              <h3>Flexible Income</h3>
              <p>
                Generate passive income on your schedule. Set your own prices, 
                availability, and rental terms. Earn money while helping others.
              </p>
              <ul>
                <li>Set your own daily rates</li>
                <li>Control availability and terms</li>
                <li>Multiple payment options</li>
                <li>Secure, insured transactions</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="about-section">
          <h2>Our Values</h2>
          
          <div className="values-grid">
            <div className="value-item">
              <div className="value-icon">🌱</div>
              <h4>Sustainability</h4>
              <p>
                By sharing resources instead of everyone buying their own, we reduce 
                manufacturing demand, decrease waste, and promote a circular economy 
                that benefits everyone.
              </p>
            </div>

            <div className="value-item">
              <div className="value-icon">🤝</div>
              <h4>Trust & Safety</h4>
              <p>
                We prioritize the safety and security of our community through 
                verification systems, secure payments, and comprehensive support 
                for every transaction.
              </p>
            </div>

            <div className="value-item">
              <div className="value-icon">💡</div>
              <h4>Innovation</h4>
              <p>
                We're constantly improving our platform with cutting-edge technology 
                and innovative features to provide the best possible experience 
                for our community.
              </p>
            </div>

            <div className="value-item">
              <div className="value-icon">🌍</div>
              <h4>Community First</h4>
              <p>
                Every feature we build is designed to strengthen local communities, 
                foster connections, and create mutual benefit for all participants.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="about-section">
          <h2>Why Choose Share My Shed?</h2>
          
          <div className="benefits-content">
            <div className="benefits-column">
              <h3>📈 For Item Owners</h3>
              <div className="benefit-list">
                <div className="benefit-item">
                  <strong>Earn Passive Income</strong>
                  <p>Transform unused items into a reliable income stream</p>
                </div>
                <div className="benefit-item">
                  <strong>Flexible Control</strong>
                  <p>Set your own prices, terms, and availability</p>
                </div>
                <div className="benefit-item">
                  <strong>Secure Transactions</strong>
                  <p>Secure payment processing with multiple convenient payment options</p>
                </div>
                <div className="benefit-item">
                  <strong>Community Building</strong>
                  <p>Meet neighbors and build local connections</p>
                </div>
              </div>
            </div>

            <div className="benefits-column">
              <h3>🛠️ For Renters</h3>
              <div className="benefit-list">
                <div className="benefit-item">
                  <strong>Affordable Access</strong>
                  <p>Use expensive tools without the high purchase cost</p>
                </div>
                <div className="benefit-item">
                  <strong>Try Before Buying</strong>
                  <p>Test items before making purchasing decisions</p>
                </div>
                <div className="benefit-item">
                  <strong>No Storage Hassle</strong>
                  <p>Access tools when needed without storage concerns</p>
                </div>
                <div className="benefit-item">
                  <strong>Local Convenience</strong>
                  <p>Find items nearby from trusted community members</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="about-section">
          <h2>Our Story</h2>
          
          <div className="story-content">
            <div className="story-text">
              <p>
                Share My Shed was born from a simple observation: most of us own expensive tools 
                and equipment that sit unused for months at a time. Meanwhile, others in our 
                community need these exact items for short-term projects but can't justify the 
                purchase cost.
              </p>
              <p>
                We realized that the sharing economy had transformed transportation and accommodation, 
                but hadn't yet addressed the massive potential in shared ownership of tools and equipment. 
                Our platform bridges this gap, creating a win-win scenario for everyone involved.
              </p>
              <p>
                Today, Share My Shed is building stronger, more sustainable communities by helping 
                neighbors share resources, reduce waste, and generate income from items that would 
                otherwise collect dust.
              </p>
            </div>
            
            <div className="story-highlight">
              <div className="highlight-icon">💡</div>
              <h4>Did You Know?</h4>
              <p>
                The average drill is used for just 13 minutes over its entire lifetime. 
                Imagine if you could earn money from yours during the other 99.97% of the time!
              </p>
            </div>
          </div>
        </section>

        {/* Future Vision Section */}
        <section className="about-section">
          <h2>Looking Forward</h2>
          
          <div className="vision-content">
            <div className="vision-cards">
              <div className="vision-card">
                <h4>🚀 Platform Growth</h4>
                <p>
                  Expanding to cover more categories of items, from vehicles and machinery 
                  to specialized equipment and electronics.
                </p>
              </div>
              
              <div className="vision-card">
                <h4>🔒 Enhanced Security</h4>
                <p>
                  Implementing advanced security features for automated, secure rentals 
                  and expanding payment processing capabilities.
                </p>
              </div>
              
              <div className="vision-card">
                <h4>🌍 Global Community</h4>
                <p>
                  Building a worldwide network of sharing communities that promotes 
                  sustainability and economic opportunity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="about-section cta-section">
          <div className="cta-content">
            <h2>Ready to Unlock Your Earning Potential?</h2>
            <p>
              Join thousands of people who are already earning money from their unused items 
              while helping build stronger, more sustainable communities.
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={handleStartListing}>
                {user ? 'Start Listing Items' : 'Sign Up to Start Listing'}
              </button>
              <button className="cta-button secondary" onClick={handleBrowseItems}>
                Browse Available Items
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About; 